import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ContractAgreement from '../components/contracts/ContractAgreement';
import { CONTRACT_VERSION } from '../constants/contract';
import { useUser } from '../contexts/UserContext';
import { useTheme } from '../context/ThemeContext';

const PrivateRoute: React.FC = () => {
  const { user: contextUser, setUser } = useUser();
  const { isDarkMode } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresContractAcceptance, setRequiresContractAcceptance] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState<{ email?: string; id?: number | null } | null>(null);
  const [checkingContract, setCheckingContract] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkAuthStatus = async () => {
      try {
        console.log(`Checking authentication with API URL: ${api.defaults.baseURL}`);

        // Always check authentication with backend via cookie
        const response = await api.get('/api/users/me');

        if (response.data && isMounted) {
          console.log('User authenticated successfully:', response.data.email);

          const userPayload = {
            email: response.data.Email,
            Role: response.data.Role,
            role: response.data.Role,
            id: response.data.UserID,
            companyID: response.data.CompanyID,
            distributorID: response.data.DistributorID || null,
            organizationID: response.data.OrganizationID || null,
            clinicID: response.data.ClinicID || null,
            CanViewPHI: response.data.CanViewPHI === true || response.data.CanViewPHI === 1,
            canViewPHI: response.data.CanViewPHI === true || response.data.CanViewPHI === 1,
            Name: response.data.Name || undefined,
            Username: response.data.Username || undefined,
          };

          setUser(userPayload);
          setIsAuthenticated(true);
          setError(null);
          setAuthenticatedUser({ email: response.data.Email, id: response.data.UserID });

          // Check contract status from backend
          const contractSigned = response.data.ContractSigned === true || response.data.ContractSigned === 1;
          const contractVersion = response.data.ContractVersion;
          const hasCurrentVersion = contractVersion === CONTRACT_VERSION;

          // Require signature if not signed OR if signed version doesn't match current version
          const requiresSignature = !contractSigned || !hasCurrentVersion;
          
          console.log('Contract status check:', {
            contractSigned,
            contractVersion,
            currentVersion: CONTRACT_VERSION,
            requiresSignature,
          });

          setRequiresContractAcceptance(requiresSignature);
          setCheckingContract(false);
        }
      } catch (err: any) {
        if (isMounted) {
          console.error('Authentication check failed:', err);
          setUser(null);
          setIsAuthenticated(false);
          setAuthenticatedUser(null);
          setRequiresContractAcceptance(false);
          setCheckingContract(false);

          if (err.code === 'ERR_NETWORK') {
            setError('Unable to connect to the server. Please refresh or try again later.');
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Check if we have user data in context for initial state
    if (contextUser) {
      setIsLoading(true);
    } else {
      setIsAuthenticated(false);
      setCheckingContract(false);
    }

    // Always check with the backend to verify authentication
    checkAuthStatus();

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading || checkingContract) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          background: isDarkMode ? '#0F1419' : '#F2EEE7',
          color: isDarkMode ? 'rgba(242, 238, 231, 0.66)' : 'rgba(15, 20, 25, 0.62)',
          fontFamily: 'Geist, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: 13,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            border: `2px solid ${isDarkMode ? 'rgba(90, 138, 127, 0.32)' : 'rgba(90, 138, 127, 0.28)'}`,
            borderTopColor: '#5A8A7F',
            borderRadius: '50%',
            animation: 'priv-spin 0.8s linear infinite',
          }}
        />
        <span style={{ fontFamily: 'Geist Mono, ui-monospace, monospace', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          Verifying your session
        </span>
        <style>{`@keyframes priv-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          background: isDarkMode ? '#0F1419' : '#F2EEE7',
          fontFamily: 'Geist, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 10px',
            border: '1px solid rgba(180, 72, 72, 0.32)',
            background: 'rgba(180, 72, 72, 0.14)',
            color: '#B44848',
            fontFamily: 'Geist Mono, ui-monospace, monospace',
            fontSize: 10.5,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          Connection Error /
        </div>
        <div
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontStyle: 'italic',
            fontSize: 17,
            lineHeight: 1.55,
            textAlign: 'center',
            maxWidth: 420,
            color: isDarkMode ? 'rgba(242, 238, 231, 0.86)' : 'rgba(15, 20, 25, 0.78)',
          }}
        >
          {error}
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            height: 44,
            padding: '0 22px',
            background: isDarkMode ? '#F2EEE7' : '#0F1419',
            color: isDarkMode ? '#0F1419' : '#F8F6F2',
            border: 'none',
            fontFamily: 'Geist, Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background 0.18s ease',
            marginTop: 8,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#3F6E63'; e.currentTarget.style.color = '#F8F6F2'; }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isDarkMode ? '#F2EEE7' : '#0F1419';
            e.currentTarget.style.color = isDarkMode ? '#0F1419' : '#F8F6F2';
          }}
        >
          Retry Connection
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const handleContractAcceptance = async () => {
    // Refetch user data to get updated contract status
    try {
      const response = await api.get('/api/users/me');
      
      if (response.data) {
        const contractSigned = response.data.ContractSigned === true || response.data.ContractSigned === 1;
        const contractVersion = response.data.ContractVersion;
        const hasCurrentVersion = contractVersion === CONTRACT_VERSION;
        
        if (contractSigned && hasCurrentVersion) {
          setRequiresContractAcceptance(false);
          console.log('✅ Contract acceptance verified, allowing access to platform');
        } else {
          console.warn('⚠️ Contract status not updated correctly. Rechecking...');
          // Force recheck
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Error verifying contract acceptance:', err);
      // Still allow access if verification fails (prevents blocking users)
      setRequiresContractAcceptance(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/logout', {}, { withCredentials: true });
    } catch (logoutError) {
      console.warn('Logout request failed while exiting contract agreement.', logoutError);
    } finally {
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  if (requiresContractAcceptance) {
    return (
      <ContractAgreement
        onAccept={handleContractAcceptance}
        onLogout={handleLogout}
        userEmail={authenticatedUser?.email ?? undefined}
      />
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
